import type { DateTimeValidationError } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useField, useForwardingRef } from '@adflow/frontend/util-common';

import type { FormControlProps } from '../../FormControl';
import { FormControl } from '../../FormControl';

import type { SelectorDateTimeProps } from './SelectorDateTime';
import { SelectorDateTime } from './SelectorDateTime';

export interface SelectorDateTimeWithControlProps
	extends
		Omit<SelectorDateTimeProps, 'value' | 'onChange'>,
		Pick<FormControlProps, 'helperStyle' | 'helperExtra' | 'outerStyle'> {
	name: string;
	label?: string;
}

/**
 * A react-hook-form input which works over a valid ISO date string.
 */
export function SelectorDateTimeWithControl(
	props: SelectorDateTimeWithControlProps
) {
	const { name, label, helperStyle, helperExtra, outerStyle, ...other } = props;
	const { dateOnly, ...restOther } = other;

	const { t } = useTranslation();

	const selectorErrorMessages = useMemo<
		Record<
			Exclude<
				DateTimeValidationError,
				// invalidDate just means the user hasn't finished filling out the date
				'invalidDate' | null
			>,
			string
		>
	>(
		() => ({
			disableFuture: t('validations:DateTime disableFuture'),
			disablePast: t('validations:DateTime disablePast'),

			shouldDisableDate: t('validations:DateTime shouldDisableDate'),
			shouldDisableMonth: t('validations:DateTime shouldDisableMonth'),
			shouldDisableYear: t('validations:DateTime shouldDisableYear'),
			minDate: restOther.minDate
				? t('validations:DateTime minDate', {
						minDate: restOther.minDate.toLocaleString(DateTime.DATE_FULL),
					})
				: t('validations:DateTime minDate generic'),
			maxDate: restOther.maxDate
				? t('validations:DateTime maxDate', {
						maxDate: restOther.maxDate.toLocaleString(DateTime.DATE_FULL),
					})
				: t('validations:DateTime maxDate generic'),

			minutesStep: t('validations:DateTime minutesStep', {
				minutesStep: restOther.minutesStep,
			}),
			minTime: t('validations:DateTime minTime', {
				minTime: restOther.minTime?.toLocaleString(DateTime.TIME_SIMPLE),
			}),
			maxTime: t('validations:DateTime maxTime', {
				maxTime: restOther.maxTime?.toLocaleString(DateTime.TIME_SIMPLE),
			}),
			'shouldDisableTime-hours': t(
				'validations:DateTime shouldDisableTime-hours'
			),
			'shouldDisableTime-minutes': t(
				'validations:DateTime shouldDisableTime-minutes'
			),
			'shouldDisableTime-seconds': t(
				'validations:DateTime shouldDisableTime-seconds'
			),
		}),
		[
			restOther.maxDate,
			restOther.maxTime,
			restOther.minDate,
			restOther.minTime,
			restOther.minutesStep,
			t,
		]
	);

	const [selectorError, setSelectorError] =
		useState<DateTimeValidationError>(null);

	const selectorErrorMessage =
		selectorError === null || selectorError === 'invalidDate'
			? null
			: selectorErrorMessages[selectorError];

	const {
		field: { onChange, value, ref },
		fieldState: { invalid },
		errorMessage,
	} = useField<string>({ name });

	const inputRef = useForwardingRef<null | HTMLInputElement>(null, [
		ref,
		props.inputRef,
	]);

	const error = invalid || (!!selectorError && selectorError !== 'invalidDate');

	// For date-only fields, parse the date in local timezone to avoid timezone shifts
	// When a date is saved as "2025-12-20T00:00:00.000Z", we want to display it as
	// December 20th in the user's local timezone, not as December 19th (if they're behind UTC)
	const parsedValue = useMemo(() => {
		if (!value) return null;

		if (dateOnly) {
			// Extract just the date part (YYYY-MM-DD) and create a DateTime at start of day in local timezone
			const dateMatch = /^(\d{4}-\d{2}-\d{2})/u.exec(value);
			if (dateMatch && dateMatch?.[1]) {
				return DateTime.fromISO(dateMatch?.[1]).startOf('day');
			}
			// Fallback to regular parsing if format is unexpected
			return DateTime.fromISO(value).startOf('day');
		}

		return DateTime.fromISO(value);
	}, [value, dateOnly]);

	return (
		<FormControl
			label={label}
			error={error}
			helperText={selectorErrorMessage ?? errorMessage}
			helperStyle={helperStyle}
			helperExtra={helperExtra || undefined}
			outerStyle={outerStyle}
		>
			<SelectorDateTime
				{...restOther}
				dateOnly={dateOnly}
				inputRef={inputRef}
				value={parsedValue}
				onChange={(newValue, context) => {
					if (newValue === null) {
						onChange('');
						return;
					}

					if (!context.validationError) {
						if (dateOnly) {
							// For date-only fields, save as midnight UTC for the selected date
							// Extract the date components and create a UTC DateTime at midnight
							// This ensures the date is saved consistently regardless of timezone
							const year = newValue.year;
							const month = newValue.month;
							const day = newValue.day;
							onChange(DateTime.utc(year, month, day, 0, 0, 0, 0).toISO());
						} else {
							onChange(newValue.toISO());
						}
					}
				}}
				onError={(error_) => {
					setSelectorError(error_);
				}}
				error={error}
			/>
		</FormControl>
	);
}
